import { createCookie } from 'react-router';
import { createTypedCookie } from 'remix-utils/typed-cookie';
import { z } from 'zod';

// 2025-05-19, jonas:
// Wir sollten die Cookie Funktionalität in server- und client-seitige funktionalität aufteilen.
// Die Cookie funktionalitäten von `react-router` und `remix-utils/typed-cookie` sind nur für den server geeignet.

const isSecure = process.env.NODE_ENV === 'production';

export const TRANSGOURMET_TRACE_ID_COOKIE_NAME = 'tg_trace-id';

function readCookieClientSide(name: string): string | undefined {
  return readRawCookie(document.cookie, name);
}
export function getTransgourmetTraceIdClientSide(): string | undefined {
  let transgourmetTraceId: string | undefined = undefined;
  try {
    const tracingIdEncoded = readCookieClientSide(TRANSGOURMET_TRACE_ID_COOKIE_NAME);
    if (tracingIdEncoded) {
      transgourmetTraceId = atob(decodeURIComponent(tracingIdEncoded));
      // Remove the double quotes if they exist
      transgourmetTraceId = transgourmetTraceId.replace(/^"|"$/g, '');
    }
  } catch {
    /* ignore */
  }
  return transgourmetTraceId;
}

// GK AIR tracking consent cookie (WEB-3920)
// Non-httpOnly: written client-side from the Usercentrics consent state (GkAirConsentSync)
// and read server-side to steer the GK AIR calls. Value is the plain string '1' (consent)
// or '0' (no consent) — we read it raw (no JSON) to avoid typed-cookie encode/decode ambiguity.
export const GK_AIR_CONSENT_COOKIE_NAME = 'tg_gkair-consent';

/**
 * Reads a raw cookie value from a cookie string (a request `Cookie` header server-side,
 * `document.cookie` client-side). Returns the undecoded value, or undefined when absent.
 */
export function readRawCookie(cookieHeader: string | null, name: string): string | undefined {
  if (!cookieHeader) return undefined;
  const cookies = cookieHeader.split(';').map(cookie => cookie.trim());
  const targetCookie = cookies.find(cookie => cookie.startsWith(`${name}=`));
  if (targetCookie) {
    return targetCookie.substring(targetCookie.indexOf('=') + 1);
  }
  return undefined;
}

/**
 * Reads the GK AIR tracking consent from the document cookie (client-side).
 * Returns `undefined` when the cookie was never set, so "unknown" stays distinguishable
 * from an explicit `false`.
 */
export function getGkAirTrackingConsentClientSide(): boolean | undefined {
  const value = readCookieClientSide(GK_AIR_CONSENT_COOKIE_NAME);
  if (value === '1') return true;
  if (value === '0') return false;
  return undefined;
}

/**
 * Writes the GK AIR tracking consent to the document cookie (client-side).
 * Mirrors the attributes of `tg_trace-id`: 1 year, root path, Lax, Secure in production.
 */
export function setGkAirTrackingConsentClientSide(tracking: boolean): void {
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  let cookie = `${GK_AIR_CONSENT_COOKIE_NAME}=${tracking ? '1' : '0'}; path=/; max-age=${maxAge}; SameSite=Lax`;
  if (isSecure) cookie += '; Secure';
  document.cookie = cookie;
}

// Locale cookie
const untypedLocaleCookie = createCookie('tg_lng', {
  path: '/',
  sameSite: 'lax',
  secure: isSecure,
  httpOnly: true,
  maxAge: 60 * 60 * 24 * 365
});
const localeSchema = z.enum(['de', 'fr', 'it']).nullable();
export const localeCookie = createTypedCookie({
  cookie: untypedLocaleCookie,
  schema: localeSchema
});

// SSO check cookie
export const untypedSsoCheckCookie = createCookie('tg_sso_check', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 5
});
const ssoCheckCookieSchema = z
  .object({
    checked: z.boolean()
  })
  .nullable();
export const ssoCheckCookie = createTypedCookie({
  cookie: untypedSsoCheckCookie,
  schema: ssoCheckCookieSchema
});

// PKCE-, Settings- und LoginInfo-Cookie liegen in cookies.server.ts: sie sind mit dem
// COOKIE_SECRET signiert, und das Secret darf nicht in den Client-Bundle-Graphen geraten.

// OAuth retry cookie — bounds restart attempts when Keycloak returns transient
// errors (e.g. temporarily_unavailable) so we don't loop on a real outage.
const untypedOauthRetryCookie = createCookie('tg_oauth_retry', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 60
});
const oauthRetrySchema = z
  .object({
    count: z.number().int().min(0).max(5)
  })
  .nullable();
export const oauthRetryCookie = createTypedCookie({
  cookie: untypedOauthRetryCookie,
  schema: oauthRetrySchema
});

// Redirect cookie

export const untypedLoginRedirectCookie = createCookie('tg_login-redirect', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 60 * 5 // 5 minutes - prevents stale redirect URLs
});
const loginRedirectCookieSchema = z
  .object({
    redirectUrl: z.string(),
    silentAuth: z.boolean()
  })
  .nullable();
export const loginRedirectCookie = createTypedCookie({
  cookie: untypedLoginRedirectCookie,
  schema: loginRedirectCookieSchema
});

// ID for Request Tracing

const untypedTransgourmetTraceIdCookie = createCookie(TRANSGOURMET_TRACE_ID_COOKIE_NAME, {
  secure: isSecure,
  sameSite: 'lax'
  // kein httpOnly: wird client-seitig gelesen (getTransgourmetTraceIdClientSide)
});
const transgourmetTraceIdSchema = z.string().nullable();
export const transgourmetTraceIdCookie = createTypedCookie({
  cookie: untypedTransgourmetTraceIdCookie,
  schema: transgourmetTraceIdSchema
});

// OCI Credentials cookie

const untypedOciCallbackUrlCookie = createCookie('tg_oci_credentials', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax'
});
const ociCredentialsSchema = z.url().nullable();
export const ociCallbackUrlCookie = createTypedCookie({
  cookie: untypedOciCallbackUrlCookie,
  schema: ociCredentialsSchema
});

// Anonymous settings cookie

const untypedAnonymousSettingsCookie = createCookie('tg_anonymous-settings', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax'
});
const anonymousSettingsSchema = z
  .object({
    tileView: z.boolean()
  })
  .nullable();
export const anonymousSettingsCookie = createTypedCookie({
  cookie: untypedAnonymousSettingsCookie,
  schema: anonymousSettingsSchema
});

// cc location selection cookie

const untypedCcLocationSelectionCookie = createCookie('tg_cc-location-selection', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 60 * 60 * 24 * 365 // 1 year
});
const anonymousCcLocationSelectionSchema = z
  .object({
    ccId: z.number()
  })
  .nullable();

export const ccLocationSelectionCookie = createTypedCookie({
  cookie: untypedCcLocationSelectionCookie,
  schema: anonymousCcLocationSelectionSchema
});

// ExternalArticlesSettingsCookie

const untypedExternalArticlesSettingsCookie = createCookie('tg_externalArticles-Settings', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 60 * 60 * 24 * 365 // 1 year
});
export const anonymousExternalArticlesSettingsSchema = z
  .object({
    articleSortOrder: z.enum(['ARTTXT', 'ARTNR']),
    articleGroupBy: z.enum(['SUPPLIER', 'CATEGORY', 'LEDGERACCOUNT'])
  })
  .nullable();

export const externalArticleSettingsCookie = createTypedCookie({
  cookie: untypedExternalArticlesSettingsCookie,
  schema: anonymousExternalArticlesSettingsSchema
});

// SalesOrderSetSettingsCookie

const untypedSalesOrderSetSettingsCookie = createCookie('tg_salesOrderSet-Settings', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax',
  maxAge: 60 * 60 * 24 * 365 // 1 year
});

export const anonymousSalesOrderSetSettingsSchema = z
  .object({
    articleGroupBy: z.enum(['HWG', 'HAGR', 'BRAND', 'LEDGERACCOUNT']),
    articleSortBy: z.enum(['ARTTXT', 'ARTNR'])
  })
  .nullable();

export const salesOrderSetSettingsCookie = createTypedCookie({
  cookie: untypedSalesOrderSetSettingsCookie,
  schema: anonymousSalesOrderSetSettingsSchema
});

// salesOrderSetFilter cookie

const untypedSalesOrderSetCookie = createCookie('tg_salesorderset-filter', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax'
});
const salesOrderSetCookieSchema = z
  .object({
    active: z.boolean()
  })
  .nullable();
export const salesOrderSetCookieCookie = createTypedCookie({
  cookie: untypedSalesOrderSetCookie,
  schema: salesOrderSetCookieSchema
});

// success order cookie

const untypedSuccessOrderCookie = createCookie('tg_success-order', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax'
});
const successOrderSchema = z.array(z.number()).nullable();

export const successOrderCookie = createTypedCookie({
  cookie: untypedSuccessOrderCookie,
  schema: successOrderSchema
});

// scanPool id cookie

const untypedScanPoolIdCookie = createCookie('tg_scanpool-id', {
  secure: isSecure,
  httpOnly: true,
  sameSite: 'lax'
});
const anonymousScanPoolIdSchema = z
  .object({
    scanId: z.string()
  })
  .nullable();

export const scanPoolIdCookie = createTypedCookie({
  cookie: untypedScanPoolIdCookie,
  schema: anonymousScanPoolIdSchema
});
